import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { FeatureFlag } from '@ndvr/auth-api';
import { ReportingSettingsOverrides } from '@ndvr/reporting-api';

import { TAB_PANEL_PADDING_BOTTOM, TAB_PANEL_PADDING_X } from '@/components/RoutingTabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useRequiredParams } from '@/utils';
import { cn } from '@/utils/cn';
import { useFeatureFlag } from '@/views/auth/useFeatureFlag';

import {
    useHouseholdReportingSettings,
    useResetHouseholdReportingSettings,
    useSaveHouseholdReportingSettings,
} from './reporting-settings-queries';
import { hasReportingSettingsOverrides, reportingSettingsOverridesEqual } from './reporting-settings-state';
import { ReportingSettingsEditor } from './ReportingSettingsEditor';

export const HouseholdReportingSettingsPage = () => {
    const reportingEnabled = useFeatureFlag(FeatureFlag.CC_REPORTING);

    if (reportingEnabled === undefined) {
        return null;
    }

    if (!reportingEnabled) {
        return <Navigate to="../../portfolio" relative="path" replace />;
    }

    // The Reporting tab is registered with noPadding (the reporting page pads itself), so this
    // page applies the tab panel's usual padding itself.
    return (
        <div className={cn('pt-6', TAB_PANEL_PADDING_X, TAB_PANEL_PADDING_BOTTOM)}>
            <HouseholdReportingSettingsPageInternal />
        </div>
    );
};

const HouseholdReportingSettingsPageInternal = () => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const settingsQuery = useHouseholdReportingSettings(globalAccountGUID);
    const saveMutation = useSaveHouseholdReportingSettings();
    const resetMutation = useResetHouseholdReportingSettings();
    const [overrides, setOverrides] = useState<ReportingSettingsOverrides>({});
    // Distinguishes user edits from the not-yet-hydrated initial draft: hydration from
    // the server must win until the user actually changes something (including after
    // navigating to a different household), and after that a background refetch must
    // never clobber the draft. A comparison against server data cannot tell those two
    // states apart.
    const [hasUserEdits, setHasUserEdits] = useState(false);

    useEffect(() => {
        setHasUserEdits(false);
    }, [globalAccountGUID]);

    useEffect(() => {
        if (settingsQuery.data && !hasUserEdits) {
            setOverrides(settingsQuery.data.overrides);
        }
    }, [settingsQuery.data, hasUserEdits]);

    const isDirty =
        hasUserEdits &&
        !!settingsQuery.data &&
        !reportingSettingsOverridesEqual(overrides, settingsQuery.data.overrides);
    const isCustom = hasReportingSettingsOverrides(overrides);
    const isSaving = saveMutation.isPending || resetMutation.isPending;

    // Recomputed from a draft-vs-server comparison on every edit so that reverting the
    // draft to the stored overrides re-enables server synchronization instead of
    // latching forever.
    const editOverrides = (next: ReportingSettingsOverrides) => {
        setOverrides(next);
        setHasUserEdits(!settingsQuery.data || !reportingSettingsOverridesEqual(next, settingsQuery.data.overrides));
    };

    const cancelChanges = () => {
        if (settingsQuery.data) {
            setOverrides(settingsQuery.data.overrides);
            setHasUserEdits(false);
        }
    };

    const saveChanges = () => {
        if (isDirty) {
            saveMutation.mutate(
                { globalAccountGUID, overrides },
                {
                    onSuccess: (saved) => {
                        setOverrides(saved.overrides);
                        setHasUserEdits(false);
                    },
                },
            );
        }
    };

    if (settingsQuery.isError) {
        return (
            <div className="text-destructive py-12 text-center text-sm">
                Failed to load household reporting settings.
                <Button variant="link" className="ml-2 h-auto p-0" onClick={() => void settingsQuery.refetch()}>
                    Retry
                </Button>
            </div>
        );
    }

    if (settingsQuery.isPending || !settingsQuery.data) {
        return <div className="text-cocoa-500 py-12 text-center text-sm">Loading reporting settings...</div>;
    }

    return (
        <div className="mx-auto flex w-full max-w-5xl flex-col pb-6">
            <div className="mb-7 flex flex-col gap-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <p className="text-cocoa-700 text-base">Household configuration</p>
                        <Badge className="bg-gold-100 text-gold-600 border-0 px-3 py-1">
                            {isCustom ? 'Custom' : 'Tenant default'}
                        </Badge>
                    </div>
                    <Button
                        variant="link"
                        className="text-seafoam-600 hover:text-seafoam-700 h-auto p-0"
                        disabled={isSaving || (!settingsQuery.data.custom && !isDirty)}
                        onClick={() =>
                            resetMutation.mutate(globalAccountGUID, {
                                onSuccess: (saved) => {
                                    setOverrides(saved.overrides);
                                    setHasUserEdits(false);
                                },
                            })
                        }
                    >
                        {resetMutation.isPending ? 'Resetting...' : 'Reset to tenant default'}
                    </Button>
                </div>
                <p className="text-cocoa-400 text-sm">
                    This household always inherits the tenant configuration. Changes below are stored only as individual
                    household overrides.
                </p>
            </div>

            <ReportingSettingsEditor
                baseSettings={settingsQuery.data.tenantSettings}
                overrides={overrides}
                disabled={isSaving}
                onOverridesChange={editOverrides}
            />

            <div className="mt-8 flex min-h-10 items-center gap-3">
                {isDirty && <p className="text-cocoa-500 mr-auto text-sm">You have unsaved changes.</p>}
                <Button variant="outline2" disabled={!isDirty || isSaving} onClick={cancelChanges}>
                    Cancel
                </Button>
                <Button variant="highlighted" disabled={!isDirty || isSaving} onClick={saveChanges}>
                    {saveMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
            </div>
        </div>
    );
};
